import React, { useMemo } from 'react'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OFFER_TYPE_BUNDLE_AND_SAVE_SLUG } from 'constants/offer'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import FormatCurrency from 'components/Common/FormatCurrency'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  BundleOfferPackageOptionView,
  getAvailableBundlePackages,
  areBundlePricesLoading,
} from 'selectors/offerPage/bundledOfferPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { capitalise } from 'lib/string/stringUtils'
import { min, sum } from 'lib/array/arrayUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import Group from 'components/utils/Group'
import DesktopRelatedTravelItems from '../RelatedTravelItems/DesktopRelatedTravelItems'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'

const StyledLayoutContainer = styled(LayoutContainer)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding-top: ${rem(12)};
  padding-bottom: ${rem(12)};
`

const PriceSummary = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  ${mediaQueryUp.tablet} {
    text-align: right;
  }
`

const HiddenTablet = styled.span`
  display: none;

  ${mediaQueryUp.desktop} {
    display: inline;
  }
`

interface Props {
  packageOptions: { [bundlePackageId: string]: BundleOfferPackageOptionView };
  onViewPackageOptions: () => void;
  offer?: App.BundleOffer;
  isHeadlessMode: boolean;
  isCalendarLoading?: boolean;
}

function BundleAndSaveHeader(props: Props) {
  const {
    onViewPackageOptions,
    packageOptions,
    isHeadlessMode,
    offer,
    isCalendarLoading,
  } = props

  const availableForPurchase = useIsAvailableForPurchase()

  const packageView = useMemo((): BundleOfferPackageOptionView => {
    const minPackageView = min(Object.values(packageOptions).filter((o) => o.capacityValid && o.hotelAvailable), (o) => o.hotelPrice)
    return minPackageView ?? Object.values(packageOptions)[0]
  }, [packageOptions])

  const {
    hotelPrice,
    propertyFees = 0,
  } = packageView

  const allPackages = useMemo(() => {
    return (packageView?.options ?? []).map(p => p.package)
  }, [packageView])

  const duration = useMemo(() => {
    return sum(allPackages, (p) => p.duration)
  }, [allPackages])

  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const durationLabel = `${pluralizeToString('night', duration)}, 2 hotels from`
  const totalPrice = Math.ceil(hotelPrice + (propertyFees ?? 0))

  return (<Pane>
    <StyledLayoutContainer>
      <Group fullWidth direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={16}>
        {!isHeadlessMode && <DesktopRelatedTravelItems offer={offer} />}
        <Group fullWidth direction="horizontal" verticalAlign="center" horizontalAlign="space-between" tabletHorizontalAlign="end" gap={16}>
          <PriceSummary>
            <LoadingIndicator visible={isCalendarLoading} floating opaque />
            {!availableForPurchase && <BodyText variant="large" weight="bold">
              This offer has<CSSBreakpoint as="br" max="mobile" /> SOLD OUT
            </BodyText>}
            {availableForPurchase && showPrices && <>
              <Caption variant="medium" wrap="no-wrap">{capitalise(durationLabel.toLowerCase())}</Caption>
              <div>
                <Heading as="span" variant="heading6">
                  <FormatCurrency value={totalPrice} />
                </Heading>
                <Caption variant="medium" as="span">/bundle</Caption>
              </div>
            </>}
          </PriceSummary>
          {!availableForPurchase && <TextButton kind="primary" to={`/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`}>
            Browse all lux bundles
          </TextButton>}
          {availableForPurchase &&
            <TextButton kind="primary" onClick={onViewPackageOptions}>
              View&nbsp;<HiddenTablet>bundle&nbsp;</HiddenTablet>options
            </TextButton>
          }
        </Group>
      </Group>
    </StyledLayoutContainer>
  </Pane>)
}

export default withOfferPageAndReduxState((state, appState) => ({
  packageOptions: getAvailableBundlePackages(state, appState),
  offer: state.offer,
  isCalendarLoading: areBundlePricesLoading(appState, state),
  isHeadlessMode: appState.system.headlessMode,
}))(BundleAndSaveHeader)
